/**
 * Contributions Section — collapsible within Step 3 (Investments)
 *
 * Captures pre-retirement pension and ISA contributions per person.
 * Live tax relief calculation shown inline.
 */

import { useState, useCallback } from 'react';
import type { ContributionPersonData, PersonData } from '../../types/wizard';
import './ContributionsSection.css';

const ANNUAL_ALLOWANCE = 60_000;
const MPAA_LIMIT = 10_000;
const ISA_ALLOWANCE = 20_000;
const BASIC_RATE_RELIEF = 0.25; // 25% gross-up on net contribution

interface ContributionsSectionProps {
  persons: PersonData[];
  contributions: { [personIndex: string]: ContributionPersonData };
  onChange: (contributions: { [personIndex: string]: ContributionPersonData }) => void;
}

function defaultContribution(person: PersonData): ContributionPersonData {
  return {
    makingContributions: false,
    monthlyPersonalContribution: 0,
    includeEmployer: false,
    monthlyEmployerContribution: 0,
    annualIsaContribution: 0,
    contributionEndDate: person.retirementDate || '',
  };
}

function formatGBP(amount: number): string {
  return amount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
}

export function ContributionsSection({ persons, contributions, onChange }: Readonly<ContributionsSectionProps>) {
  const hasActiveContributions = persons.some((_, i) => contributions[`person${i}`]?.makingContributions);
  // A3 (#1592 phase 2): the standalone "no ongoing contributions" AA/MPAA
  // warning below lives inside this collapsible section — a person whose
  // prior-tax-year contributions alone breach the limit, but who never
  // ticks "making regular contributions", must still see it without first
  // opening a section that otherwise has nothing to show them.
  const hasPriorContributionBreach = persons.some(person => {
    if (!person) return false;
    const limit = (person.lsaUsedPrePlan ?? 0) > 0 ? MPAA_LIMIT : ANNUAL_ALLOWANCE;
    return (person.priorPensionContributions ?? 0) > limit;
  });
  const [isOpen, setIsOpen] = useState(hasActiveContributions || hasPriorContributionBreach);

  const updatePerson = useCallback((personIndex: string, update: Partial<ContributionPersonData>) => {
    const person = persons[parseInt(personIndex.replace('person', ''), 10)];
    const current = contributions[personIndex] || defaultContribution(person);
    onChange({
      ...contributions,
      [personIndex]: { ...current, ...update },
    });
  }, [contributions, onChange, persons]);

  return (
    <div className="contributions-section">
      <button
        type="button"
        className="contributions-toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className="contributions-toggle-icon">{isOpen ? '−' : '+'}</span>
        <span className="contributions-toggle-label">
          Add ongoing contributions
        </span>
        <span className="contributions-toggle-hint">
          Still working? Add pension and ISA contributions here
        </span>
      </button>

      {isOpen && (
        <div className="contributions-body">
          {persons.map((person, idx) => {
            const key = `person${idx}`;
            const data = contributions[key] || defaultContribution(person);
            const taxRelief = data.monthlyPersonalContribution * BASIC_RATE_RELIEF;
            const monthlyGross = data.monthlyPersonalContribution + taxRelief;
            const annualGross = monthlyGross * 12;
            const annualEmployer = data.monthlyEmployerContribution * 12;
            const totalPensionAnnual = annualGross + annualEmployer;
            const hasLsaUsed = (person.lsaUsedPrePlan ?? 0) > 0;
            const aaLimit = hasLsaUsed ? MPAA_LIMIT : ANNUAL_ALLOWANCE;
            // A3 (#1592 phase 2): count pension contributions already made
            // this tax year (priorPensionContributions) toward the limit —
            // previously this warning used totalPensionAnnual alone, so
            // £50k already contributed plus £15k/year ongoing showed
            // nothing here even though the combined £65k breaches the £60k
            // Annual Allowance.
            const priorPensionContributions = person.priorPensionContributions ?? 0;
            const combinedPensionContributions = totalPensionAnnual + priorPensionContributions;
            const aaExceeded = combinedPensionContributions > aaLimit;
            const isaExceeded = data.annualIsaContribution > ISA_ALLOWANCE;

            // Validation: if making contributions, at least one amount must be non-zero
            const hasAnyAmount = data.monthlyPersonalContribution > 0
              || (data.includeEmployer && data.monthlyEmployerContribution > 0)
              || data.annualIsaContribution > 0;
            const showNoAmountError = data.makingContributions && !hasAnyAmount;
            const showEndDateError = data.makingContributions && hasAnyAmount && !data.contributionEndDate;

            return (
              <div key={key} className="contribution-person">
                {persons.length > 1 && (
                  <h4 className="contribution-person-name">{person.fullName || `Person ${idx + 1}`}</h4>
                )}

                <label className="contribution-checkbox-label">
                  <input
                    type="checkbox"
                    checked={data.makingContributions}
                    onChange={e => updatePerson(key, { makingContributions: e.target.checked })}
                  />
                  I am making regular pension or ISA contributions
                </label>

                {/* A3 (#1592 phase 2): shown even when this box is unchecked — the
                    exact target user is retiring with no further contributions
                    planned, having already contributed up to the limit earlier
                    this tax year. */}
                {!data.makingContributions && aaExceeded && (
                  <div className="warning-message" role="alert">
                    {hasLsaUsed
                      ? `The ${formatGBP(priorPensionContributions)} you've already contributed this tax year exceeds the ${formatGBP(MPAA_LIMIT)} limit that applies because you have already taken pension income. Contributing more may result in a tax charge — check with your pension provider.`
                      : `The ${formatGBP(priorPensionContributions)} you've already contributed this tax year exceeds the ${formatGBP(ANNUAL_ALLOWANCE)} Annual Allowance. You may face a tax charge on the excess. If you have unused allowance from prior years, carry-forward may apply.`
                    }
                  </div>
                )}

                {data.makingContributions && (
                  <div className="contribution-fields">
                    {showNoAmountError && (
                      <div className="contribution-error" role="alert">
                        Enter at least one contribution amount below, or uncheck the box above.
                      </div>
                    )}

                    {/* Personal pension contribution */}
                    <h5 className="contribution-subheader">Pension contributions</h5>
                    <div className="form-group">
                      <label htmlFor={`contrib-personal-${key}`} className="form-label">
                        Your monthly pension contribution
                      </label>
                      <div className="contribution-input-row">
                        <span className="contribution-currency">£</span>
                        <input
                          id={`contrib-personal-${key}`}
                          type="number"
                          min="0"
                          step="10"
                          className="form-input contribution-input"
                          value={data.monthlyPersonalContribution || ''}
                          onChange={e => updatePerson(key, {
                            monthlyPersonalContribution: parseFloat(e.target.value) || 0,
                          })}
                        />
                        <span className="contribution-period-label">/month</span>
                      </div>
                      {data.monthlyPersonalContribution > 0 && (
                        <div className="tax-relief-display">
                          <div className="tax-relief-row">
                            <span>You contribute:</span>
                            <span>{formatGBP(data.monthlyPersonalContribution)}/month</span>
                          </div>
                          <div className="tax-relief-row tax-relief-highlight">
                            <span>Tax relief adds:</span>
                            <span>{formatGBP(taxRelief)}/month</span>
                          </div>
                          <div className="tax-relief-row tax-relief-total">
                            <span>Total into pension:</span>
                            <span>{formatGBP(monthlyGross)}/month ({formatGBP(annualGross)}/year)</span>
                          </div>
                          <p className="tax-relief-note">
                            Based on basic rate (20%) tax relief for Relief at Source schemes.
                            Higher and additional rate taxpayers may claim further relief via
                            self-assessment.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Employer contribution */}
                    <label className="contribution-checkbox-label contribution-employer-toggle">
                      <input
                        type="checkbox"
                        checked={data.includeEmployer}
                        onChange={e => updatePerson(key, { includeEmployer: e.target.checked })}
                      />
                      My employer also contributes
                    </label>

                    {data.includeEmployer && (
                      <div className="form-group">
                        <label htmlFor={`contrib-employer-${key}`} className="form-label">
                          Employer monthly contribution
                        </label>
                        <div className="contribution-input-row">
                          <span className="contribution-currency">£</span>
                          <input
                            id={`contrib-employer-${key}`}
                            type="number"
                            min="0"
                            step="10"
                            className="form-input contribution-input"
                            value={data.monthlyEmployerContribution || ''}
                            onChange={e => updatePerson(key, {
                              monthlyEmployerContribution: parseFloat(e.target.value) || 0,
                            })}
                          />
                          <span className="contribution-period-label">/month</span>
                        </div>
                        {annualEmployer > 0 && (
                          <span className="help-text">
                            Combined total: {formatGBP(monthlyGross + data.monthlyEmployerContribution)}/month
                            ({formatGBP(totalPensionAnnual)}/year)
                          </span>
                        )}
                      </div>
                    )}

                    {/* ISA contribution */}
                    <h5 className="contribution-subheader">ISA contributions</h5>
                    <div className="form-group">
                      <label htmlFor={`contrib-isa-${key}`} className="form-label">
                        Annual ISA contribution (limit: {formatGBP(ISA_ALLOWANCE)})
                      </label>
                      <div className="contribution-input-row">
                        <span className="contribution-currency">£</span>
                        <input
                          id={`contrib-isa-${key}`}
                          type="number"
                          min="0"
                          step="100"
                          className="form-input contribution-input"
                          value={data.annualIsaContribution || ''}
                          onChange={e => updatePerson(key, {
                            annualIsaContribution: parseFloat(e.target.value) || 0,
                          })}
                        />
                        <span className="contribution-period-label">/year</span>
                      </div>
                    </div>

                    {/* Contribution end date */}
                    <div className="form-group">
                      <label htmlFor={`contrib-end-${key}`} className="form-label">
                        Stop contributing on
                      </label>
                      <input
                        id={`contrib-end-${key}`}
                        type="date"
                        className="form-input contribution-date"
                        value={data.contributionEndDate}
                        onChange={e => updatePerson(key, { contributionEndDate: e.target.value })}
                      />
                      <span className="help-text">Defaults to your retirement date.</span>
                      {showEndDateError && (
                        <span className="error-message">A stop date is required when making contributions.</span>
                      )}
                    </div>

                    {/* Warnings */}
                    {aaExceeded && (
                      <div className="warning-message" role="alert">
                        {priorPensionContributions > 0
                          ? (hasLsaUsed
                            ? `Your ongoing pension contributions (${formatGBP(totalPensionAnnual)}/year) plus the ${formatGBP(priorPensionContributions)} you've already contributed this tax year (${formatGBP(combinedPensionContributions)} total) exceed the ${formatGBP(MPAA_LIMIT)} limit that applies because you have already taken pension income. Contributing more may result in a tax charge — check with your pension provider.`
                            : `Your ongoing pension contributions (${formatGBP(totalPensionAnnual)}/year) plus the ${formatGBP(priorPensionContributions)} you've already contributed this tax year (${formatGBP(combinedPensionContributions)} total) exceed the ${formatGBP(ANNUAL_ALLOWANCE)} Annual Allowance. You may face a tax charge on the excess. If you have unused allowance from prior years, carry-forward may apply.`)
                          : (hasLsaUsed
                            ? `Your total pension contributions (${formatGBP(totalPensionAnnual)}/year) exceed the ${formatGBP(MPAA_LIMIT)} limit that applies because you have already taken pension income. Contributing more may result in a tax charge — check with your pension provider.`
                            : `Total pension contributions (${formatGBP(totalPensionAnnual)}/year) exceed the ${formatGBP(ANNUAL_ALLOWANCE)} Annual Allowance. You may face a tax charge on the excess. If you have unused allowance from prior years, carry-forward may apply.`)
                        }
                      </div>
                    )}
                    {isaExceeded && (
                      <div className="warning-message" role="alert">
                        ISA contributions ({formatGBP(data.annualIsaContribution)}) exceed
                        the annual ISA allowance ({formatGBP(ISA_ALLOWANCE)}).
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <p className="contributions-disclaimer">
            Projections are illustrative and based on your assumptions. Tax relief
            calculations are simplified for modelling purposes. If you have complex
            pension arrangements, seek advice from an FCA-authorised financial adviser.
          </p>
        </div>
      )}
    </div>
  );
}
